import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Building2,
  Users,
  GraduationCap,
  Plus,
  Search,
  Edit,
  Trash2,
  Power,
  PowerOff,
  Loader2,
  Shield,
  Activity,
  AlertTriangle,
} from 'lucide-react';
import { toast } from 'sonner';
import { Navigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const roleNames = {
  superadmin: 'Süper Admin',
  tenant_owner: 'Kurum Sahibi',
  center_director: 'Merkez Müdürü',
  reading_coach: 'Okuma Koçu',
  teacher: 'Öğretmen',
  intervention_specialist: 'Müdahale Uzmanı',
  counselor: 'Rehber Öğretmen',
  parent: 'Veli',
  student: 'Öğrenci',
};

const StatCard = ({ icon: Icon, title, value, subtitle, color = 'primary' }) => (
  <Card>
    <CardContent className="pt-6">
      <div className="flex items-center gap-4">
        <div className={`h-12 w-12 rounded-xl flex items-center justify-center ${
          color === 'primary' ? 'bg-primary/10' :
          color === 'green' ? 'bg-green-100' :
          color === 'blue' ? 'bg-blue-100' :
          color === 'yellow' ? 'bg-yellow-100' :
          'bg-primary/10'
        }`}>
          <Icon className={`h-6 w-6 ${
            color === 'primary' ? 'text-primary' :
            color === 'green' ? 'text-green-600' :
            color === 'blue' ? 'text-blue-600' :
            color === 'yellow' ? 'text-yellow-600' :
            'text-primary'
          }`} />
        </div>
        <div>
          <p className="text-sm text-muted-foreground">{title}</p>
          <p className="text-3xl font-bold">{value}</p>
          {subtitle && <p className="text-xs text-muted-foreground">{subtitle}</p>}
        </div>
      </div>
    </CardContent>
  </Card>
);

export const SuperadminPage = () => {
  const { user, getToken } = useAuth();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [activeTab, setActiveTab] = useState('dashboard');
  
  // Tenants state
  const [tenants, setTenants] = useState([]);
  const [tenantSearch, setTenantSearch] = useState('');
  const [showTenantDialog, setShowTenantDialog] = useState(false);
  const [editingTenant, setEditingTenant] = useState(null);
  const [tenantForm, setTenantForm] = useState({ name: '', slug: '', contact_email: '', contact_phone: '', address: '' });
  
  // Users state
  const [users, setUsers] = useState([]);
  const [userSearch, setUserSearch] = useState('');
  const [userTenantFilter, setUserTenantFilter] = useState('all');
  const [showUserDialog, setShowUserDialog] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [userForm, setUserForm] = useState({ email: '', name: '', role: 'teacher', tenant_id: '' });
  
  const [saving, setSaving] = useState(false);

  // Check if user is superadmin
  const isSuperadmin = user?.role === 'superadmin' || user?.email === 'kayhankarli@gmail.com';

  // Helper function to get auth headers
  const getAuthHeaders = useCallback(() => {
    const token = getToken();
    return token ? { 'Authorization': `Bearer ${token}` } : {};
  }, [getToken]);
  
  // Debug: Log user and token info
  useEffect(() => {
    const token = localStorage.getItem('session_token');
    console.log('========================');
  }, [user, isSuperadmin]);

  useEffect(() => {
    if (isSuperadmin) {
      fetchStats();
      fetchTenants();
      fetchUsers();
    }
  }, [isSuperadmin]);

  const fetchStats = async () => {
    try {
      const headers = getAuthHeaders();
      const response = await fetch(`${API_URL}/api/superadmin/stats`, { 
        credentials: 'include',
        headers
      });
      if (response.ok) {
        setStats(await response.json());
      } else {
        console.error('fetchStats failed:', response.status);
      }
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTenants = async () => {
    try {
      const params = new URLSearchParams();
      if (tenantSearch) params.append('search', tenantSearch);
      
      const headers = getAuthHeaders();
      const response = await fetch(`${API_URL}/api/superadmin/tenants?${params}`, { 
        credentials: 'include',
        headers
      });
      if (response.ok) {
        const data = await response.json();
        setTenants(data.items || []);
      }
    } catch (error) {
      console.error('Failed to fetch tenants:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const params = new URLSearchParams();
      if (userSearch) params.append('search', userSearch);
      if (userTenantFilter !== 'all') params.append('tenant_id', userTenantFilter);
      params.append('limit', '100');
      
      const headers = getAuthHeaders();
      const response = await fetch(`${API_URL}/api/superadmin/users?${params}`, { 
        credentials: 'include',
        headers
      });
      if (response.ok) {
        const data = await response.json();
        setUsers(data.items || []);
      }
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  };

  const handleCreateTenant = async () => {
    if (!tenantForm.name || !tenantForm.slug) {
      toast.error('Kurum adı ve slug zorunludur');
      return;
    }
    
    setSaving(true);
    try {
      const headers = { 'Content-Type': 'application/json', ...getAuthHeaders() };
      
      const response = await fetch(`${API_URL}/api/superadmin/tenants`, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify(tenantForm),
      });
      
      
      if (response.ok) {
        toast.success('Kurum oluşturuldu');
        setShowTenantDialog(false);
        setTenantForm({ name: '', slug: '', contact_email: '', contact_phone: '', address: '' });
        fetchTenants();
        fetchStats();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Hata oluştu');
      }
    } catch (error) {
      console.error('Fetch error:', error);
      toast.error('Bir hata oluştu');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateTenant = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/superadmin/tenants/${editingTenant.tenant_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        credentials: 'include',
        body: JSON.stringify(tenantForm),
      });
      
      if (response.ok) {
        toast.success('Kurum güncellendi');
        setShowTenantDialog(false);
        setEditingTenant(null);
        fetchTenants();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Hata oluştu');
      }
    } catch (error) {
      toast.error('Bir hata oluştu');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleTenantStatus = async (tenant) => {
    try {
      const endpoint = tenant.is_active 
        ? `${API_URL}/api/superadmin/tenants/${tenant.tenant_id}`
        : `${API_URL}/api/superadmin/tenants/${tenant.tenant_id}/activate`;
      
      const response = await fetch(endpoint, {
        method: tenant.is_active ? 'DELETE' : 'POST',
        credentials: 'include',
        headers: getAuthHeaders(),
      });
      
      if (response.ok) {
        toast.success(tenant.is_active ? 'Kurum devre dışı bırakıldı' : 'Kurum aktifleştirildi');
        fetchTenants();
        fetchStats();
      }
    } catch (error) {
      toast.error('Bir hata oluştu');
    }
  };

  const handleCreateUser = async () => {
    if (!userForm.email || !userForm.name || !userForm.tenant_id) {
      toast.error('Email, ad ve kurum zorunludur');
      return;
    }
    
    setSaving(true);
    try {
      const headers = { 'Content-Type': 'application/json', ...getAuthHeaders() };
      console.log('Making user request with headers:', headers);
      
      const response = await fetch(`${API_URL}/api/superadmin/users`, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify(userForm),
      });
      
      console.log('User response status:', response.status);
      
      if (response.ok) {
        toast.success('Kullanıcı oluşturuldu');
        setShowUserDialog(false);
        setUserForm({ email: '', name: '', role: 'teacher', tenant_id: '' });
        fetchUsers();
        fetchStats();
      } else {
        const error = await response.json();
        console.error('User error response:', error);
        toast.error(error.detail || 'Hata oluştu');
      }
    } catch (error) {
      console.error('User fetch error:', error);
      toast.error('Bir hata oluştu');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateUser = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/superadmin/users/${editingUser.user_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        credentials: 'include',
        body: JSON.stringify({ role: userForm.role, is_active: userForm.is_active }),
      });
      
      if (response.ok) {
        toast.success('Kullanıcı güncellendi');
        setShowUserDialog(false);
        setEditingUser(null);
        fetchUsers();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Hata oluştu');
      }
    } catch (error) {
      toast.error('Bir hata oluştu');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleUserStatus = async (targetUser) => {
    try {
      const response = await fetch(`${API_URL}/api/superadmin/users/${targetUser.user_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        credentials: 'include',
        body: JSON.stringify({ is_active: !targetUser.is_active }),
      });
      
      if (response.ok) {
        toast.success(targetUser.is_active ? 'Kullanıcı devre dışı bırakıldı' : 'Kullanıcı aktifleştirildi');
        fetchUsers();
      }
    } catch (error) {
      toast.error('Bir hata oluştu');
    }
  };

  const openEditTenant = (tenant) => {
    setEditingTenant(tenant);
    setTenantForm({
      name: tenant.name,
      slug: tenant.slug,
      contact_email: tenant.settings?.contact_email || '',
      contact_phone: tenant.settings?.contact_phone || '',
      address: tenant.settings?.address || '',
    });
    setShowTenantDialog(true);
  };

  const openEditUser = (targetUser) => {
    setEditingUser(targetUser);
    setUserForm({
      email: targetUser.email,
      name: targetUser.name,
      role: targetUser.role,
      tenant_id: targetUser.tenant_id,
      is_active: targetUser.is_active,
    });
    setShowUserDialog(true);
  };

  // Redirect if not superadmin
  if (!isSuperadmin) {
    return <Navigate to="/dashboard" replace />;
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="superadmin-page">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
          <Shield className="h-6 w-6 text-white" />
        </div>
        <div>
          <h1 className="text-2xl font-heading font-bold text-primary-900">Süper Admin Paneli</h1>
          <p className="text-muted-foreground">Platform yönetimi ve izleme</p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="dashboard">Dashboard</TabsTrigger>
          <TabsTrigger value="tenants">Kurumlar</TabsTrigger>
          <TabsTrigger value="users">Kullanıcılar</TabsTrigger>
        </TabsList>

        {/* Dashboard Tab */}
        <TabsContent value="dashboard" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard
              icon={Building2}
              title="Toplam Kurum"
              value={stats?.tenants?.total || 0}
              subtitle={`${stats?.tenants?.active || 0} aktif`}
              color="primary"
            />
            <StatCard
              icon={Users}
              title="Toplam Kullanıcı"
              value={stats?.users?.total || 0}
              subtitle={`${stats?.users?.active || 0} aktif`}
              color="blue"
            />
            <StatCard
              icon={GraduationCap}
              title="Toplam Öğrenci"
              value={stats?.students?.total || 0}
              subtitle={`${stats?.students?.active || 0} aktif`}
              color="green"
            />
            <StatCard
              icon={Activity}
              title="Bugünkü Giriş"
              value={stats?.activity?.logins_today || 0}
              color="yellow"
            />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Users by Role */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Rol Dağılımı</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {Object.entries(stats?.users?.by_role || {}).map(([role, count]) => (
                    <div key={role} className="flex items-center justify-between">
                      <span className="text-sm">{roleNames[role] || role}</span>
                      <Badge variant="secondary">{count}</Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Risk Distribution */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Risk Dağılımı (Tüm Öğrenciler)</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-red-600">Yüksek Risk</span>
                    <Badge variant="destructive">{stats?.students?.by_risk?.high || 0}</Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-yellow-600">Orta Risk</span>
                    <Badge className="bg-yellow-100 text-yellow-700">{stats?.students?.by_risk?.medium || 0}</Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-green-600">Düşük/Normal</span>
                    <Badge className="bg-green-100 text-green-700">
                      {(stats?.students?.by_risk?.low || 0) + (stats?.students?.by_risk?.none || 0)}
                    </Badge>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Tenants Tab */}
        <TabsContent value="tenants" className="space-y-4">
          <div className="flex items-center justify-between gap-4">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Kurum ara..."
                value={tenantSearch}
                onChange={(e) => setTenantSearch(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && fetchTenants()}
                className="pl-10"
              />
            </div>
            <Button onClick={() => { setEditingTenant(null); setTenantForm({ name: '', slug: '', contact_email: '', contact_phone: '', address: '' }); setShowTenantDialog(true); }}>
              <Plus className="mr-2 h-4 w-4" />
              Yeni Kurum
            </Button>
          </div>

          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Kurum Adı</TableHead>
                  <TableHead>Slug</TableHead>
                  <TableHead>Kullanıcılar</TableHead>
                  <TableHead>Öğrenciler</TableHead>
                  <TableHead>Durum</TableHead>
                  <TableHead className="text-right">İşlemler</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {tenants.map(tenant => (
                  <TableRow key={tenant.tenant_id}>
                    <TableCell className="font-medium">{tenant.name}</TableCell>
                    <TableCell className="text-muted-foreground">{tenant.slug}</TableCell>
                    <TableCell>{tenant.user_count}</TableCell>
                    <TableCell>{tenant.student_count}</TableCell>
                    <TableCell>
                      <Badge variant={tenant.is_active ? 'default' : 'secondary'}>
                        {tenant.is_active ? 'Aktif' : 'Pasif'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button variant="ghost" size="sm" onClick={() => openEditTenant(tenant)}>
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => handleToggleTenantStatus(tenant)}
                        >
                          {tenant.is_active ? <PowerOff className="h-4 w-4 text-red-500" /> : <Power className="h-4 w-4 text-green-500" />}
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {tenants.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                      Kurum bulunamadı
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        {/* Users Tab */}
        <TabsContent value="users" className="space-y-4">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-4 flex-1">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Kullanıcı ara..."
                  value={userSearch}
                  onChange={(e) => setUserSearch(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && fetchUsers()}
                  className="pl-10"
                />
              </div>
              <Select value={userTenantFilter} onValueChange={(v) => { setUserTenantFilter(v); }}>
                <SelectTrigger className="w-[200px]">
                  <SelectValue placeholder="Kurum Filtrele" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tüm Kurumlar</SelectItem>
                  {tenants.map(t => (
                    <SelectItem key={t.tenant_id} value={t.tenant_id}>{t.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button variant="outline" onClick={fetchUsers}>Filtrele</Button>
            </div>
            <Button onClick={() => { setEditingUser(null); setUserForm({ email: '', name: '', role: 'teacher', tenant_id: '' }); setShowUserDialog(true); }}>
              <Plus className="mr-2 h-4 w-4" />
              Yeni Kullanıcı
            </Button>
          </div>

          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Ad</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Rol</TableHead>
                  <TableHead>Kurum</TableHead>
                  <TableHead>Durum</TableHead>
                  <TableHead className="text-right">İşlemler</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map(u => (
                  <TableRow key={u.user_id}>
                    <TableCell className="font-medium">{u.name}</TableCell>
                    <TableCell className="text-muted-foreground">{u.email}</TableCell>
                    <TableCell>
                      <Badge variant="outline">{roleNames[u.role] || u.role}</Badge>
                    </TableCell>
                    <TableCell>{u.tenant_name || '-'}</TableCell>
                    <TableCell>
                      <Badge variant={u.is_active ? 'default' : 'secondary'}>
                        {u.is_active ? 'Aktif' : 'Pasif'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button variant="ghost" size="sm" onClick={() => openEditUser(u)}>
                          <Edit className="h-4 w-4" />
                        </Button>
                        {u.email !== 'kayhankarli@gmail.com' && (
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => handleToggleUserStatus(u)}
                          >
                            {u.is_active ? <PowerOff className="h-4 w-4 text-red-500" /> : <Power className="h-4 w-4 text-green-500" />}
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {users.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                      Kullanıcı bulunamadı
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Tenant Dialog */}
      <Dialog open={showTenantDialog} onOpenChange={setShowTenantDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingTenant ? 'Kurum Düzenle' : 'Yeni Kurum'}</DialogTitle>
            <DialogDescription>
              {editingTenant ? 'Kurum bilgilerini güncelleyin.' : 'Yeni bir kurum oluşturun.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium">Kurum Adı *</label>
              <Input
                value={tenantForm.name}
                onChange={(e) => setTenantForm({...tenantForm, name: e.target.value})}
                placeholder="Bilge Okuma Merkezi"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Slug (URL) *</label>
              <Input
                value={tenantForm.slug}
                onChange={(e) => setTenantForm({...tenantForm, slug: e.target.value.toLowerCase().replace(/\s+/g, '-')})}
                placeholder="bilge-okuma"
                disabled={!!editingTenant}
              />
            </div>
            <div>
              <label className="text-sm font-medium">İletişim Email</label>
              <Input
                type="email"
                value={tenantForm.contact_email}
                onChange={(e) => setTenantForm({...tenantForm, contact_email: e.target.value})}
                placeholder="iletisim@kurum.com"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Telefon</label>
              <Input
                value={tenantForm.contact_phone}
                onChange={(e) => setTenantForm({...tenantForm, contact_phone: e.target.value})}
                placeholder="0212 123 45 67"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Adres</label>
              <Input
                value={tenantForm.address}
                onChange={(e) => setTenantForm({...tenantForm, address: e.target.value})}
                placeholder="İstanbul, Türkiye"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTenantDialog(false)}>İptal</Button>
            <Button onClick={editingTenant ? handleUpdateTenant : handleCreateTenant} disabled={saving}>
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {editingTenant ? 'Güncelle' : 'Oluştur'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* User Dialog */}
      <Dialog open={showUserDialog} onOpenChange={setShowUserDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingUser ? 'Kullanıcı Düzenle' : 'Yeni Kullanıcı'}</DialogTitle>
            <DialogDescription>
              {editingUser ? 'Kullanıcı bilgilerini güncelleyin.' : 'Yeni bir kullanıcı oluşturun.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {!editingUser && (
              <>
                <div>
                  <label className="text-sm font-medium">Email *</label>
                  <Input
                    type="email"
                    value={userForm.email}
                    onChange={(e) => setUserForm({...userForm, email: e.target.value})}
                    placeholder="kullanici@email.com"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Ad Soyad *</label>
                  <Input
                    value={userForm.name}
                    onChange={(e) => setUserForm({...userForm, name: e.target.value})}
                    placeholder="Ad Soyad"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Kurum *</label>
                  <Select value={userForm.tenant_id} onValueChange={(v) => setUserForm({...userForm, tenant_id: v})}>
                    <SelectTrigger>
                      <SelectValue placeholder="Kurum seçin" />
                    </SelectTrigger>
                    <SelectContent>
                      {tenants.filter(t => t.is_active).map(t => (
                        <SelectItem key={t.tenant_id} value={t.tenant_id}>{t.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}
            <div>
              <label className="text-sm font-medium">Rol</label>
              <Select value={userForm.role} onValueChange={(v) => setUserForm({...userForm, role: v})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(roleNames).filter(([k]) => k !== 'superadmin').map(([value, label]) => (
                    <SelectItem key={value} value={value}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUserDialog(false)}>İptal</Button>
            <Button onClick={editingUser ? handleUpdateUser : handleCreateUser} disabled={saving}>
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {editingUser ? 'Güncelle' : 'Oluştur'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SuperadminPage;
